import axios from '@/plugins/axiosInstance'
import { socialMedia } from '@/networking/urlManager'
import { responseHandlerService } from '@/plugins/responseHandlerService'

/**
 * Generate content from AI based on trend and parameters
 * @param {Object} data - Content generation parameters
 * @param {Object} data.trend - Trend object
 * @param {Array} data.selectedPlatforms - Selected platforms to generate content for
 * @param {number} data.selectedAspectRatio - Selected aspect ratio
 * @param {number} data.languageId - Selected language ID
 * @param {Array} data.keywords - Keywords for content generation
 * @param {boolean} data.hasVideo - Whether to include video content
 * @param {string} data.videoDescription - Video description (if hasVideo is true)
 * @param {string} assetId - Asset ID
 * @param {Object} cancelToken - Axios cancel token
 * @returns {Promise<Object>} Generated content object
 */
export const generateContentFromTrend = async (data, assetId, cancelToken) => {
  try {
    const payload = {
      assetId: assetId,
      trendId: data.trend.id,
      trendName: data.trend.name,
      languageId: data.languageId,
      keywords: data.keywords,
      size: data.selectedAspectRatio,
      trendKeyword: data.trend.name,
      newsUrl: data.trend.newsUrl || null,
      videoDescription: data.hasVideo ? data.videoDescription : null,
      hasVideo: data.hasVideo ? 1 : 0,
    }

    console.log('Trend content generation payload:', payload)

    const response = await axios.post(socialMedia.postGenerate, payload, {
      cancelToken: cancelToken,
    })

    return processApiResponse(response)
  } catch (error) {
    // Check if the request was canceled by the user
    if (axios.isCancel(error)) {
      console.log('Request canceled:', error.message)
      return null
    }

    console.error('Error generating content from trend:', error)
    responseHandlerService.handleError('Failed to generate content. Please try again.')
    throw error
  }
}

/**
 * Generate content from manual form input
 * @param {Object} formData - Manual content form data
 * @param {string} assetId - Asset ID
 * @param {Object} cancelToken - Axios cancel token
 * @returns {Promise<Object>} Generated content object
 */
export const generateManualContent = async (formData, assetId, cancelToken) => {
  try {
    // Make sure we're sending the correct size parameter
    const size = formData.aspectRatio || 3 // Default to 16:9 (3) if not specified

    const payload = {
      assetId: assetId,
      languageId: formData.languageId,
      keywords: formData.keywords,
      size: size,
      videoDescription: formData.hasVideo ? formData.videoDescription : null,
      hasVideo: formData.hasVideo ? 1 : 0,
    }

    const response = await axios.post(socialMedia.postGenerate, payload, {
      cancelToken: cancelToken,
    })

    return processApiResponse(response)
  } catch (error) {
    // Check if the request was canceled by the user
    if (axios.isCancel(error)) {
      console.log('Request canceled:', error.message)
      return null
    }

    console.error('Error generating content from manual input:', error)
    responseHandlerService.handleError('Failed to generate content. Please try again.')
    throw error
  }
}

/**
 * Process API response to create content object
 * @param {Object} response - API response
 * @returns {Object} Formatted content object
 */
const processApiResponse = (response) => {
  if (!response?.data?.data) {
    throw new Error('Invalid API response')
  }

  const content = response.data.data

  // Create basic content object
  return {
    message: content.socialMediaContent || '',
    hashtags: parseHashtags(content.socialMediaHashtags),
    link: content.link || '',
    media: [],
    hasVideo: !!content.videoUrl || content.hasVideo === true || content.hasVideo === 1,
    videoDescription: content.videoDescription || null,
    videoUrl: content.videoUrl || null,
  }
}

/**
 * Parse hashtags from API response
 * @param {String|Array} hashtagsData - Hashtags data
 * @returns {Array} Parsed hashtags array
 */
const parseHashtags = (hashtagsData) => {
  if (!hashtagsData) return []

  try {
    return typeof hashtagsData === 'string' ? JSON.parse(hashtagsData) : hashtagsData
  } catch (e) {
    console.error('Error parsing hashtags:', e)
    return []
  }
}
